import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Briefcase } from 'lucide-react';
import { assessments } from '../utils/api';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ email: '', password: '' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(formData);
            toast.success('Login successful!');
            
            if (user.user_type === 'company') {
                // Beta: only demo employers can access the employer dashboard.
                const betaMode = process.env.REACT_APP_BETA_MODE === 'true';
                if (betaMode && !user.is_demo) {
                    navigate('/beta-restricted');
                } else {
                    navigate('/company/dashboard');
                }
            } else if (user.user_type === 'candidate') {
                // Try to fetch assessment status with one retry. On hard failure,
                // do NOT silently route to the dashboard (would bypass the
                // assessment gate). Surface the error and keep the user on the
                // login page so they can retry.
                let statusRes = null;
                let attempts = 0;
                while (attempts < 2 && !statusRes) {
                    try {
                        statusRes = await assessments.getStatus();
                    } catch (e) {
                        attempts += 1;
                        if (attempts < 2) {
                            await new Promise((r) => setTimeout(r, 500));
                        }
                    }
                }
                if (!statusRes) {
                    toast.error('Could not verify your assessment status. Please try logging in again.');
                    return;
                }
                if (!statusRes.data.completed) {
                    navigate('/candidate/assessment');
                } else {
                    navigate('/candidate/dashboard');
                }
            } else if (user.user_type === 'admin') {
                navigate('/admin/dashboard');
            }
        } catch (error) {
            toast.error(error.userMessage || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Sampler | Log In</title>
                <meta name="description" content="Log in to your Sampler account." />
            </Helmet>
            <div className="w-full max-w-md">
                <div className="bg-white rounded-3xl shadow-sm p-8 border border-purple-200/50">
                    <div className="flex items-center space-x-2 mb-8">
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Sign In
                    </span>
                    <h2 className="text-3xl font-bold mb-1 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="login-heading">Welcome Back</h2>
                    <p className="text-slate-500 mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>Log in to your account to continue</p>

                    {/* Beta banner — remove by setting REACT_APP_BETA_MODE=false */}
                    {process.env.REACT_APP_BETA_MODE === 'true' && (
                        <div className="mb-6 px-4 py-3 rounded-xl bg-purple-50 border border-purple-200 text-sm text-purple-700 text-center leading-snug" data-testid="beta-banner" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            This is our candidate beta. Full launch and employer access coming soon!
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email Address</Label>
                            <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="mt-1.5 rounded-xl border-purple-200/50 focus:border-[#8B5CF6] focus:ring-[#8B5CF6]" data-testid="email-input" />
                        </div>
                        
                        <div>
                            <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password</Label>
                            <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required className="mt-1.5 rounded-xl border-purple-200/50 focus:border-[#8B5CF6] focus:ring-[#8B5CF6]" data-testid="password-input" />
                        </div>
                        
                        <Button type="submit" className="w-full btn-primary h-12" disabled={loading} data-testid="login-submit-btn">
                            {loading ? 'Logging in...' : 'Log In'}
                        </Button>
                        <div className="text-right mt-2">
                            <Link to="/forgot-password" className="text-sm text-[#8B5CF6] hover:underline font-semibold" data-testid="forgot-password-link">
                                Forgot password?
                            </Link>
                        </div>
                    </form>
                    
                    <p className="mt-8 text-center text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Don't have an account? <Link to="/register" className="text-[#8B5CF6] font-semibold hover:underline" data-testid="register-link">Register</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
